import { CircularProgress, Pagination, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import { DataGrid, GridToolbarColumnsButton } from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { DataGrid_SX } from "../../Assets/customTheme/constants";
import { CustomNoRowsOverlay } from "../../Assets/customTheme/CustomNoRowsOverlay";
import {
	default as CoustomTooltip,
	default as CustomTooltip,
} from "../../Assets/customTheme/customtooltip";
import { TableColumns, TableFilte, TableReset } from "../../Assets/svg/svgicon";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { formatDateTime } from "../Common/DateFormat";
import CustomSnackbar from "../Common/snackBar";
import renderStatusBadge from "../../utils/badges";
import {MyDeleteOutlineOutlinedIcon, MyDescriptionOutlinedIcon} from "../../Assets/svg/MyIcons";
import { getMailLogsAction } from "../../Actions/MailLogsAction";
import DateRangePicker from "../Common/DateRangePicker";

const getDefaultDateRange = () => {
	const end = new Date();
	const start = new Date();
	start.setMonth(start.getMonth() - 1);

	return { from: start, to: end };
};

const CustomToolbar = ({
	statusFilter,
	moduleFilter,
	handleChangeDropdown,
	handleFilterData,
	handleResetFilters,
	selectedRange,
	onRangeChange,
	selectedRows,
	handleDeleteSelected,
	filterButtonDisabled,
	resetButtonDisabled,
}) => (
	<div className="custom-header">
		<div className="card border-rounded-0 fiter-card">
			<div className="card-body p-5 pb-0">
				<div className="row">
					<div className="col-lg-2 col-md-4 col-sm-6 mb-5">
						<FormControl className="w-100">
							<Select
								displayEmpty
								value={statusFilter}
								onChange={(e) =>
									handleChangeDropdown(e, "status")
								}
								input={<OutlinedInput />}
							>
								<MenuItem value="all">All Status</MenuItem>
								<MenuItem value="0">Pending</MenuItem>
								<MenuItem value="1">Sent</MenuItem>
								<MenuItem value="2">Failed</MenuItem>
							</Select>
						</FormControl>
					</div>

					<div className="col-lg-2 col-md-4 col-sm-6 mb-5">
						<FormControl className="w-100">
							<Select
								displayEmpty
								value={moduleFilter}
								onChange={(e) =>
									handleChangeDropdown(e, "module")
								}
								input={<OutlinedInput />}
							>
								<MenuItem value="all">All Modules</MenuItem>
								<MenuItem value="users">Users</MenuItem>
								<MenuItem value="workflow">Workflow</MenuItem>
								<MenuItem value="support_ticket">
									Support Ticket
								</MenuItem>
							</Select>
						</FormControl>
					</div>
					<div className="col-lg-3 col-md-7 col-sm-12 mb-5">
						<DateRangePicker
							selectedRange={selectedRange}
							onRangeChange={onRangeChange}
							maxDate={new Date()}
							placeholder="Select date range"
						/>
					</div>

					<div className="col-auto ms-auto mb-5">
						<div className="d-flex gap-3">
							<CustomTooltip title="Filter" placement="top">
								<span style={{ display: "inline-block" }}>
									<button
										className="btn btn-light btn-icon text-primary"
										onClick={handleFilterData}
										disabled={filterButtonDisabled}
									>
										<TableFilte />
									</button>
								</span>
							</CustomTooltip>

							<CustomTooltip title="Reset" placement="top">
								<span style={{ display: "inline-block" }}>
									<button
										className="btn btn-light btn-icon text-primary"
										onClick={handleResetFilters}
										disabled={resetButtonDisabled}
									>
										<TableReset />
									</button>
								</span>
							</CustomTooltip>

							<CoustomTooltip
								title="Column Filter"
								placement="top"
								arrow
							>
								<span style={{ display: "inline-block" }}>
									<GridToolbarColumnsButton
										slotProps={{
											tooltip: { title: "" },
											button: {
												className:
													"btn btn-light text-primary btn-icon grid-toolbar d-flex justify-content-center align-items-center p-0",
												style: {
													width: "47px",
													height: "44px",
													minWidth: "unset",
													borderRadius: "8px",
												},
												startIcon: <TableColumns />,
											},
										}}
									/>
								</span>
							</CoustomTooltip>

							{selectedRows.length > 0 && (
								<CoustomTooltip title="Delete">
									<button
										className="btn btn-outline-danger btn-icon"
										onClick={handleDeleteSelected}
									>
										<MyDeleteOutlineOutlinedIcon />
									</button>
								</CoustomTooltip>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
);

const MailLogsSection = () => {
	const [logsData, setLogsData] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);

	const [statusFilter, setStatusFilter] = useState("all");
	const [moduleFilter, setModuleFilter] = useState("all");
	const [selectedRange, setSelectedRange] = useState(getDefaultDateRange());
	const [startDate, setStartDate] = useState(
		getDefaultDateRange().from.toISOString()
	);
	const [endDate, setEndDate] = useState(
		getDefaultDateRange().to.toISOString()
	);

	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
	const [totalRecords, setTotalRecords] = useState(0);

	const [loading, setLoading] = useState(true);
	const [filterButtonDisabled, setFilterButtonDisabled] = useState(true);
	const [resetButtonDisabled, setResetButtonDisabled] = useState(true);

	const [filtersApplied, setFiltersApplied] = useState(false);

	const [selectedEmailBody, setSelectedEmailBody] = useState(null);
	const [openBodyModal, setOpenBodyModal] = useState(false);

	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const fetchMailLogs = useCallback(
		async (isFilterAction = false) => {
			setLoading(true);
			try {
			const res = await getMailLogsAction(
				currentPage,
				rowsPerPage,
				statusFilter,
				moduleFilter,
				startDate,
				endDate
			);

			setLogsData(res.data.data.rows);
			setTotalRecords(res.data.data.count);
			setSelectedRows([]);

			if (isFilterAction) {
				setFilterButtonDisabled(true);
				setResetButtonDisabled(false);
				setFiltersApplied(true);
			}
			} catch {
			setSnackbar({
				open: true,
				message: "Failed to fetch mail logs",
				severity: "error",
			});
			} finally {
			setLoading(false);
			}
		},[currentPage,rowsPerPage,statusFilter,moduleFilter,startDate,endDate]
	);


	useEffect(() => {
	if (
		!filtersApplied &&
		statusFilter === "all" &&
		moduleFilter === "all"
	) {
		fetchMailLogs();
	}
	},[fetchMailLogs,filtersApplied,statusFilter,moduleFilter,]);


	// Handle filter button click
	const handleFilterData = () => {
		setCurrentPage(1);
		fetchMailLogs(true); // Pass true to indicate this is a filter action
	};

	const handleChangeDropdown = (e, type) => {
		setFilterButtonDisabled(false);

		// Enable reset if filters were previously applied
		if (filtersApplied) {
			setResetButtonDisabled(false);
		}

		if (type === "status") setStatusFilter(e.target.value);
		if (type === "module") setModuleFilter(e.target.value);
	};

	const handleRangeChange = (range) => {
		setFilterButtonDisabled(false);

		// Enable reset if filters were previously applied
		if (filtersApplied) {
			setResetButtonDisabled(false);
		}

		if (!range) {
			setSelectedRange({ from: undefined, to: undefined });
			setStartDate(null);
			setEndDate(null);
			return;
		}

		setSelectedRange(range);

		if (range.from) {
			const year = range.from.getFullYear();
			const month = range.from.getMonth();
			const day = range.from.getDate();
			const startDateObj = new Date(
				Date.UTC(year, month, day, 0, 0, 0, 0)
			);
			setStartDate(startDateObj.toISOString());
		}

		if (range.to) {
			const year = range.to.getFullYear();
			const month = range.to.getMonth();
			const day = range.to.getDate();
			const endDateObj = new Date(
				Date.UTC(year, month, day, 23, 59, 59, 999)
			);
			setEndDate(endDateObj.toISOString());
		}
	};

	const handleResetFilters = () => {
		const defaultRange = getDefaultDateRange();

		// Reset all filter values
		setStatusFilter("all");
		setModuleFilter("all");
		setSelectedRange(defaultRange);
		setStartDate(defaultRange.from.toISOString());
		setEndDate(defaultRange.to.toISOString());

		// Reset button states
		setFilterButtonDisabled(true);
		setResetButtonDisabled(true);
		setFiltersApplied(false);

		// Reset pagination
		setCurrentPage(1);
	};

	const handleViewBody = (body) => {
		setSelectedEmailBody(body);
		setOpenBodyModal(true);
	};

	const handleCloseBodyModal = () => {
		setOpenBodyModal(false);
		setSelectedEmailBody(null);
	};


	const columns = [
		// {
		// 	field: "select",
		// 	minWidth: 50,
		// 	renderHeader: () => (
		// 		<Checkbox
		// 			checked={
		// 				logsData.length > 0 &&
		// 				selectedRows.length === logsData.length
		// 			}
		// 			onChange={(e) =>
		// 				setSelectedRows(
		// 					e.target.checked ? logsData.map((r) => r.id) : []
		// 				)
		// 			}
		// 			sx={MyCheckboxIcon}
		// 		/>
		// 	),
		// 	renderCell: (p) => (
		// 		<Checkbox
		// 			checked={selectedRows.includes(p.row.id)}
		// 			onChange={() =>
		// 				setSelectedRows((prev) =>
		// 					prev.includes(p.row.id)
		// 						? prev.filter((i) => i !== p.row.id)
		// 						: [...prev, p.row.id]
		// 				)
		// 			}
		// 			sx={MyCheckboxIcon}
		// 		/>
		// 	),
		// },
		{ field: "from", headerName: "From", flex: 1, minWidth: 150 },
		{ field: "to", headerName: "To", flex: 1, minWidth: 150 },
		{ field: "subject", headerName: "Subject", flex: 1, minWidth: 150 },
		{
			field: "body",
			headerName: "Body",
			width: 80,
			align: "center",
			headerAlign: "center",
			sortable: false,
			renderCell: (params) => (
				<CustomTooltip title="View Email Body" placement="top">
					<IconButton
						className="btn-custom-i text-primary"
						onClick={() => handleViewBody(params.row.body)}
						disabled={!params.row.body}
					>
						<MyDescriptionOutlinedIcon />
					</IconButton>
				</CustomTooltip>
			),
		},
		{
			field: "status",
			headerName: "Status",
			align: "center",
			headerAlign: "center",
			renderCell: (p) => (
				<div>
					{renderStatusBadge(
						{
							0: { title: "Pending", status: "warning" },
							1: { title: "Sent", status: "success" },
							2: { title: "Failed", status: "danger" },
						}[p.value]
					)}
					{p.row.reason && (
						<CustomTooltip title={p.row.reason} placement="top">
							<InfoOutlinedIcon color="error" fontSize="small" style={{ cursor: "pointer" }} />
						</CustomTooltip>
					)}
				</div>
			),
		},
		{
			field: "createdAt",
			headerName: "Date And Time",
			minWidth: 200,
			renderCell: (p) => formatDateTime(p.value, true),
		},
	];

	return (
		<>
			<div id="kt_content_container">
				<BreadcrumbsComponent />

				<DataGrid
					className="border-0"
					localeText={{ toolbarColumns: "" }}
					rows={logsData}
					columns={columns}
					autoHeight
					getRowId={(r) => r.id}
					hideFooterRowCount={true}
					hideFooterPagination={true}
					hideFooter={true}
					loading={loading}
					sx={DataGrid_SX}
					slots={{
						toolbar: CustomToolbar,
						noRowsOverlay: CustomNoRowsOverlay,
					}}
					components={{
						loadingOverlay: () => (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									height: "100px",
								}}
							>
								<CircularProgress
									color="primary"
									size={24}
									style={{
										marginRight: 10,
									}}
								/>
								Loading...
							</div>
						),
						noRowsOverlay: () =>
							loading ? null : (
								<div
									style={{
										display:
											!loading && logsData.length === 0
												? "block"
												: "none",
										fontWeight: "bold",
										textAlign: "center",
										padding: "20px",
									}}
								>
									No Records Found
								</div>
							),
					}}
					slotProps={{
						toolbar: {
							statusFilter,
							moduleFilter,
							handleChangeDropdown,
							handleFilterData, // Use the new handler
							handleResetFilters,
							selectedRange,
							onRangeChange: handleRangeChange,
							selectedRows,
							filterButtonDisabled,
							resetButtonDisabled,
						},
					}}
				/>

				<div className="card-footer d-flex justify-content-between mt-6">
					<FormControl variant="standard">
						<Select
							value={rowsPerPage}
							onChange={(e) => {
								setRowsPerPage(e.target.value);
								setCurrentPage(1);
							}}
						>
							{ITEMS_PER_PAGE_OPTIONS.map((o) => (
								<MenuItem key={o} value={o}>
									{o}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Pagination
						count={Math.ceil(totalRecords / rowsPerPage)}
						siblingCount={0}
						page={currentPage}
						onChange={(event, value) => {
							if (value !== currentPage) {
								setCurrentPage(value);
								setLogsData([]);
								setSelectedRows([]);
								setLoading(true);
							}
						}}
						variant="outlined"
						shape="rounded"
						className="custom-pagination mt-0 ms-5"
					/>
				</div>

				<CustomSnackbar
					{...snackbar}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
				/>

				{/* Email Body View Modal */}
				<Dialog
					open={openBodyModal}
					onClose={handleCloseBodyModal}
					maxWidth="lg"
					fullWidth
				>
					<DialogTitle className="card-header text-bg-primary fw-bold fs-2">
						Email Body
					</DialogTitle>
					
					<DialogContent>
						{selectedEmailBody && (
							<div
								dangerouslySetInnerHTML={{ __html: selectedEmailBody }}
								style={{
									fontFamily: 'Arial, sans-serif',
									fontSize: '14px',
									color: '#333',
								}}
							/>
						)}
					</DialogContent>
					
					<DialogActions className="border-top p-7 d-flex justify-content-between">
						<button
							type="button"
							onClick={handleCloseBodyModal}
							className="btn btn-sm btn-outline btn-outline-primary"
						>
							Close
						</button>
					</DialogActions>
				</Dialog>
			</div>
		</>
	);
};

export default MailLogsSection;
